import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Cookie from "js-cookie";
import { Grid, Typography, Paper, Box, CircularProgress } from "@mui/material";
import { usePDF } from "react-to-pdf";
import * as XLSX from "xlsx";
import { StyledButton, StyledCancelButton } from "components/StyledComponents/styledComponents"; // Adjust path as needed
import { format, differenceInMonths, differenceInDays } from "date-fns";
import { useMaterialUIController } from "context";
import URLs from "constants/urls";

const BalanceSheet = ({ startDate, endDate, selectedDsaId }) => {
  const formatINR = (value) =>
    Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const { toPDF, targetRef } = usePDF({
    filename: "Balance_Sheet_Report.pdf",
    options: {
      method: "save",
      resolution: 3,
      page: { margin: 10, format: "A4", orientation: "portrait" },
      canvas: { mimeType: "image/png", qualityRatio: 1 },
    },
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false); // Only load if data prop is not provided
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const jwt_token = Cookie.get("jwt_token");
  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${URLs.balanceSheet}?startDate=${startDate}&endDate=${endDate}&companyId=${selectedDsaId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${jwt_token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      setRows(data);
    } catch (err) {
      console.error("Error fetching balance sheet", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [startDate, endDate, selectedDsaId]);

  const handleExcelExport = () => {
    const liabilitySheet = rows.map((row, idx) => [
      idx + 1,
      row.liabilityLedger || "",
      row.liabilityBalance || 0,
    ]);
    const assetSheet = rows.map((row, idx) => [
      idx + 1,
      row.assetLedger || "",
      row.assetBalance || 0,
    ]);

    const maxRows = Math.max(liabilitySheet.length, assetSheet.length);
    while (liabilitySheet.length < maxRows) liabilitySheet.push(["", "", ""]);
    while (assetSheet.length < maxRows) assetSheet.push(["", "", ""]);

    const finalData = [
      ["#", "Liability Ledger", "Liability Balance", "#", "Asset Ledger", "Asset Balance"],
    ];
    for (let i = 0; i < maxRows; i++) {
      finalData.push([...liabilitySheet[i], ...assetSheet[i]]);
    }

    const ws = XLSX.utils.aoa_to_sheet(finalData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BalanceSheet");
    XLSX.writeFile(wb, "Balance_Sheet_Report.xlsx");
  };

  return (
    <Paper sx={{ p: 3 }} ref={targetRef} elevation={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={2}>
        <Typography variant="h6" gutterBottom>
          Balance Sheet
          <br />
          <span
            style={{
              backgroundColor: "#F5F5F5",
              color: "black",
              fontWeight: 600,
              borderRadius: "10px",
              padding: "4px 12px",
              display: "inline-block",
              cursor: "pointer",
              border: "1px solid #ccc",
              transition: "background-color 0.3s ease",
            }}
          >
            From {format(new Date(startDate), "dd-MMM-yyyy")} &nbsp;to&nbsp;
            {format(new Date(endDate), "dd-MMM-yyyy")} &nbsp; (
            {(() => {
              const months = differenceInMonths(new Date(endDate), new Date(startDate));
              const days = differenceInDays(new Date(endDate), new Date(startDate)) % 30;

              if (months > 0 && days > 0) {
                return `${months} month${months > 1 ? "s" : ""} ${days} day${days > 1 ? "s" : ""}`;
              } else if (months > 0) {
                return `${months} month${months > 1 ? "s" : ""}`;
              } else {
                return `${days} day${days > 1 ? "s" : ""}`;
              }
            })()}
            )
          </span>
        </Typography>

        <Box sx={{ textAlign: "center", mb: 2 }}>
          <StyledButton variant="contained" sx={{ mr: 1 }} onClick={() => toPDF()}>
            Download PDF
          </StyledButton>
          <StyledCancelButton variant="outlined" onClick={handleExcelExport}>
            Download Excel
          </StyledCancelButton>
        </Box>
      </Box>

      {/* Table Header */}
      <Grid container sx={{ border: "1px solid #ccc" }}>
        {/* Heading Row */}
        <Grid container>
          <Grid
            item
            xs={6}
            sx={{
              borderRight: "1px solid #ccc",
              p: 1,
              background: "#e0f7fa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ display: "flex", alignItems: "center", fontWeight: "bold" }}
            >
              Liabilities
            </Typography>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{
              p: 1,
              background: "#e0f7fa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ display: "flex", alignItems: "center", fontWeight: "bold" }}
            >
              Assets
            </Typography>
          </Grid>
        </Grid>

        {/* Column Headers */}
        <Grid
          item
          xs={1}
          sx={{ borderRight: "1px solid #ccc", p: 1, background: "#f5f5f5", textAlign: "center" }}
        >
          <Typography variant="subtitle2">
            <strong>S.No</strong>
          </Typography>
        </Grid>
        <Grid item xs={3} sx={{ borderRight: "1px solid #ccc", p: 1, background: "#f5f5f5" }}>
          <Typography variant="subtitle2">
            <strong>Ledger</strong>
          </Typography>
        </Grid>
        <Grid
          item
          xs={2}
          sx={{ borderRight: "1px solid #ccc", p: 1, background: "#f5f5f5", textAlign: "right" }}
        >
          <Typography variant="subtitle2">
            <strong>Balance</strong>
          </Typography>
        </Grid>
        <Grid
          item
          xs={1}
          sx={{ borderRight: "1px solid #ccc", p: 1, background: "#f5f5f5", textAlign: "center" }}
        >
          <Typography variant="subtitle2">
            <strong>S.No</strong>
          </Typography>
        </Grid>
        <Grid item xs={3} sx={{ borderRight: "1px solid #ccc", p: 1, background: "#f5f5f5" }}>
          <Typography variant="subtitle2">
            <strong>Ledger</strong>
          </Typography>
        </Grid>
        <Grid item xs={2} sx={{ p: 1, background: "#f5f5f5", textAlign: "right" }}>
          <Typography variant="subtitle2">
            <strong>Balance</strong>
          </Typography>
        </Grid>
      </Grid>

      {/* Table Body */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="300px">
          <CircularProgress />
        </Box>
      ) : (
        (() => {
          const maxLength = rows.length;
          return Array.from({ length: maxLength }).map((_, idx) => (
            <Grid
              container
              key={idx}
              sx={{ borderLeft: "1px solid #ccc", borderRight: "1px solid #ccc" }}
            >
              {/* Liabilities */}
              <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "center" }}>
                <Typography sx={{ fontSize: "14px" }}>
                  {rows[idx].liabilityLedger ? idx + 1 : ""}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ borderRight: "1px solid #ccc", p: 1 }}>
                <Typography sx={{ fontSize: "14px" }}>{rows[idx].liabilityLedger || ""}</Typography>
              </Grid>
              <Grid item xs={2} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "right" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                  {rows[idx]?.liabilityBalance != null && rows[idx]?.liabilityBalance !== 0
                    ? formatINR(Math.abs(rows[idx].liabilityBalance))
                    : rows[idx]?.liabilityBalance === 0
                    ? ""
                    : ""}
                </Typography>
              </Grid>

              {/* Assets */}
              <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "center" }}>
                <Typography sx={{ fontSize: "14px" }}>
                  {rows[idx].assetLedger ? idx + 1 : ""}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ borderRight: "1px solid #ccc", p: 1 }}>
                <Typography sx={{ fontSize: "14px" }}>{rows[idx].assetLedger || ""}</Typography>
              </Grid>
              <Grid item xs={2} sx={{ p: 1, textAlign: "right" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                  {rows[idx]?.assetBalance != null && rows[idx]?.assetBalance !== 0
                    ? formatINR(Math.abs(rows[idx].assetBalance))
                    : rows[idx]?.assetBalance === 0
                    ? ""
                    : ""}
                </Typography>
              </Grid>
            </Grid>
          ));
        })()
      )}
      <Grid container sx={{ border: "1px solid #ccc", borderTop: "none", background: "#f5f5f5" }}>
        <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }}></Grid>
        <Grid
          item
          xs={3}
          sx={{
            borderRight: "1px solid #ccc",
            p: 1,
            fontSize: "1rem",
            fontWeight: "bold",
          }}
        ></Grid>
        <Grid
          item
          xs={2}
          sx={{
            borderRight: "1px solid #ccc",
            p: 1,
            fontSize: "1rem",
            textAlign: "right",
            fontWeight: "bold",
          }}
        ></Grid>

        <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }}></Grid>
        <Grid
          item
          xs={3}
          sx={{
            fontSize: "1rem",
            fontWeight: "bold",
            borderRight: "1px solid #ccc",
            p: 1,
          }}
        ></Grid>
        <Grid
          item
          xs={2}
          sx={{ p: 1, fontSize: "1rem", textAlign: "right", fontWeight: "bold" }}
        ></Grid>

        <Grid item xs={6}></Grid>
        <Grid item xs={1}></Grid>
      </Grid>
    </Paper>
  );
};

BalanceSheet.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      liabilityLedger: PropTypes.string,
      liabilityBalance: PropTypes.number,
      assetLedger: PropTypes.string,
      assetBalance: PropTypes.number,
    })
  ),
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  selectedDsaId: PropTypes.number,
};

export default BalanceSheet;
